/**
 * Dominio de Facturación de la empresa (Fase 2) — endpoints `/billing/*`
 * (rol COMPANY_ADMIN). El backend filtra por `company_id` del JWT.
 *
 * El registro de pago es multipart (comprobante opcional): usamos FormData, que
 * `lib/api` ya soporta (no setea Content-Type para dejar el boundary al browser).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';
import type { CompanyStatus } from './auth/types';

export type BillingCycle = 'monthly' | 'semiannual' | 'yearly';
export type PaymentStatus = 'PENDING' | 'CONFIRMED' | 'REJECTED';

export interface SubscriptionPlan {
  name: string;
  price_monthly: number;
  price_yearly: number;
  currency: string;
}

/** Uso de almacenamiento de la empresa (archivos adjuntos vs cuota del plan). */
export interface StorageUsage {
  used_bytes: number;
  limit_bytes: number;
}

export interface Subscription {
  status: string;
  plan: SubscriptionPlan | null;
  /** Monto MENSUAL efectivo a pagar (override de la empresa o precio del plan). */
  effective_price_monthly?: number;
  billing_cycle: BillingCycle | null;
  current_period_end: string | null;
  company_status: CompanyStatus;
  trial_ends_at: string | null;
  storage?: StorageUsage;
  /**
   * ¿Está habilitado el cobro con TARJETA (hub Bancard vía pagos.evoluado.com)?
   * Sólo si es `true` mostramos el botón "Pagar con tarjeta". Lo decide el backend.
   */
  card_enabled: boolean;
  /** ¿Habilitado el pago manual por transferencia/depósito (con comprobante)? */
  transfer_enabled: boolean;
  /** ¿Habilitado el pago por QR (muestra la imagen del QR + la opción)? */
  qr_enabled: boolean;
}

export interface BillingPayment {
  id: string;
  amount: number;
  currency: string;
  method: string;
  reference: string | null;
  status: PaymentStatus;
  proof_url: string | null;
  created_at: string;
  paid_at: string | null;
}

export interface CreatePaymentInput {
  method: string;
  amount?: number;
  reference?: string;
  billing_cycle?: BillingCycle;
  proof?: File | null;
}

/**
 * Resultado de iniciar un cobro con tarjeta (POST /billing/pay-card): el backend
 * crea el pago PENDING contra el hub y devuelve la `checkout_url` a la que hay que
 * redirigir el navegador. El usuario NO ingresa datos de tarjeta en Tembiapo.
 */
export interface PayCardResult {
  payment_id: string;
  checkout_url: string;
  amount: number;
  currency: string;
}

/** Estado de un pago con tarjeta consultado al volver del hub (?pago=<id>). */
export interface CardPaymentStatus {
  id: string;
  status: PaymentStatus;
  amount: number;
  currency: string;
  created_at: string;
  paid_at: string | null;
}

/**
 * Formas de pago de la PLATAFORMA (datos de transferencia + QR) que la empresa
 * ve al ir a pagar. Iguales para todas las empresas; las configura el super admin.
 */
export interface PaymentMethods {
  bank_name: string;
  account: string;
  alias: string;
  holder: string;
  document: string;
  instructions: string;
  qr_url: string;
}

/** Un plan del rubro al que la empresa puede cambiar (autogestión). */
export interface AvailablePlan {
  id: string;
  name: string;
  price_monthly: number;
  price_yearly: number;
  currency: string;
  max_projects: number;
  max_users: number;
}

export interface AvailablePlans {
  current_plan_id: string;
  /** La empresa tiene un precio personalizado (override): cambiar de plan no lo altera. */
  has_custom_price: boolean;
  plans: AvailablePlan[];
}

/** Resultado de cambiar de plan (POST /billing/change-plan). */
export interface ChangePlanResult {
  ok: boolean;
  plan_id: string;
  plan_name: string;
  direction: 'upgrade' | 'downgrade' | 'lateral';
  /** Monto prorrateado a pagar por la mejora (0 si no aplica). */
  proration_amount: number;
  proration_payment_id: string | null;
  currency: string;
}

export const subscriptionKey = ['billing', 'subscription'] as const;
const paymentsKey = ['billing', 'payments'] as const;
const availablePlansKey = ['billing', 'plans'] as const;
const paymentMethodsKey = ['billing', 'payment-methods'] as const;

export function useSubscription(enabled = true) {
  return useQuery({
    queryKey: subscriptionKey,
    queryFn: () => api.get<Subscription>('/billing/subscription'),
    enabled,
  });
}

/**
 * ¿Ocultar "Facturación" en el menú? Sí cuando la empresa está ACTIVE y su monto
 * mensual efectivo es 0 (plan gratis / override sin costo): no debe pagar nada.
 * Mientras carga o si no hay dato, NO se oculta (para no esconderlo de más).
 */
export function useHideBilling(enabled = true): boolean {
  const { data } = useSubscription(enabled);
  return data?.company_status === 'ACTIVE' && data?.effective_price_monthly === 0;
}

export function useBillingPayments() {
  return useQuery({
    queryKey: paymentsKey,
    queryFn: () => api.get<BillingPayment[]>('/billing/payments'),
  });
}

/** Planes del rubro a los que la empresa puede cambiar (autogestión). */
export function useAvailablePlans(enabled = true) {
  return useQuery({
    queryKey: availablePlansKey,
    queryFn: () => api.get<AvailablePlans>('/billing/plans'),
    enabled,
  });
}

/**
 * Cambia el plan de la empresa. En upgrade a mitad de ciclo (pagando) el backend
 * genera un pago PENDIENTE por la diferencia prorrateada → refrescamos también los
 * pagos. Devuelve el detalle (dirección + monto prorrateado) para el aviso.
 */
export function useChangePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) =>
      api.post<ChangePlanResult>('/billing/change-plan', { plan_id: planId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: subscriptionKey });
      void qc.invalidateQueries({ queryKey: availablePlansKey });
      void qc.invalidateQueries({ queryKey: paymentsKey });
    },
  });
}

/** Datos de "cómo pagar" (transferencia + QR) que configura el super admin. */
export function usePaymentMethods() {
  return useQuery({
    queryKey: paymentMethodsKey,
    queryFn: () => api.get<PaymentMethods>('/billing/payment-methods'),
  });
}

export function useCreatePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePaymentInput) => {
      const form = new FormData();
      form.append('method', input.method);
      if (input.amount != null) form.append('amount', String(input.amount));
      if (input.reference) form.append('reference', input.reference);
      if (input.billing_cycle) form.append('billing_cycle', input.billing_cycle);
      if (input.proof) form.append('proof', input.proof);
      return api.post<BillingPayment>('/billing/payments', form);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: paymentsKey });
      void qc.invalidateQueries({ queryKey: subscriptionKey });
    },
  });
}

/**
 * Inicia un cobro con TARJETA en el hub externo. En éxito devuelve `checkout_url`;
 * el llamador debe redirigir el navegador (`window.location.href = checkout_url`).
 * No lleva body: el backend calcula monto/moneda desde la suscripción.
 */
export function usePayCard() {
  return useMutation({
    mutationFn: () => api.post<PayCardResult>('/billing/pay-card', {}),
  });
}

/**
 * Consulta el estado de un pago con tarjeta al volver del hub (?pago=<id>).
 * Hace polling cada 3s mientras esté PENDING, con tope de ~10 intentos, y frena
 * al llegar a un estado final (CONFIRMED/REJECTED). `enabled` sólo si hay id.
 */
export function usePayCardStatus(id: string | null) {
  return useQuery({
    queryKey: ['billing', 'pay-card', id] as const,
    queryFn: () => api.get<CardPaymentStatus>(`/billing/pay-card/${id}`),
    enabled: id != null && id !== '',
    // El backend actualiza el estado de forma asíncrona (webhook del hub); no
    // cacheamos para no mostrar un estado viejo entre montajes.
    staleTime: 0,
    gcTime: 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'CONFIRMED' || status === 'REJECTED') return false;
      // dataUpdateCount cuenta fetches OK: cortamos tras ~10 para no colgar el poll.
      if (query.state.dataUpdateCount >= 10) return false;
      return 3000;
    },
  });
}
